import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLists } from '../context/ListsContext';
import hiveService from '../services/hive';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import './ListFeed.css';

const ListDetail = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { username, listName: listNameParam } = useParams();
  const listName = listNameParam || '';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lists, loaded, deleteList, removeFromList, addToList, renameList } = useLists();

  const [removing, setRemoving] = useState(null); // username being removed
  const [deleting, setDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [addingFromSearch, setAddingFromSearch] = useState({});

  const isOwner = user?.username === username;
  const list = loaded ? lists.find(l => l.name === listName) : null;
  const memberSet = useMemo(
    () => new Set((list?.members || []).map((member) => String(member).toLowerCase())),
    [list?.members]
  );

  useEffect(() => {
    if (!isOwner || !list) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    const q = searchValue.trim().toLowerCase();
    if (q.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const matches = await hiveService.searchAccounts(q, 10);
        if (cancelled) return;
        setSearchResults(matches.filter((name) => !memberSet.has(String(name).toLowerCase())));
      } catch (_err) {
        if (cancelled) return;
        setSearchResults([]);
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchValue, isOwner, list, memberSet]);

  const handleRemoveMember = useCallback(async (memberUsername) => {
    if (!list) return;
    if (!window.confirm(`Remove @${memberUsername} from "${list.name}"?`)) return;
    setRemoving(memberUsername);
    try {
      await removeFromList(list.id, memberUsername);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setRemoving(null);
    }
  }, [list, removeFromList]);

  const handleDeleteList = useCallback(async () => {
    if (!list || list.is_favourite) return;
    if (!window.confirm(`Delete list "${list.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteList(list.id);
      navigate('/lists');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete list');
      setDeleting(false);
    }
  }, [list, deleteList, navigate]);

  const handleAddMemberFromSearch = useCallback(async (memberUsername) => {
    if (!list || !memberUsername) return;
    const normalized = String(memberUsername).trim().toLowerCase();
    if (!normalized || memberSet.has(normalized)) return;

    setAddingFromSearch((prev) => ({ ...prev, [normalized]: true }));
    try {
      await addToList(list.id, normalized);
      setSearchValue('');
      setSearchResults([]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAddingFromSearch((prev) => ({ ...prev, [normalized]: false }));
    }
  }, [list, addToList, memberSet]);

  const handleRenameSubmit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!list || !trimmed || trimmed === list.name) {
      setIsRenaming(false);
      return;
    }
    setSavingRename(true);
    try {
      await renameList(list.id, trimmed);
      setIsRenaming(false);
      navigate(`/user/${user.username}/lists/${encodeURIComponent(trimmed)}`, { replace: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename list');
    } finally {
      setSavingRename(false);
    }
  }, [list, renameValue, renameList, user?.username, navigate]);

  return (
    <div className="list-feed-page">
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />

      <div className="list-feed-header">
        <div className="list-feed-header-top">
          <Link to="/lists" className="list-feed-back">← Your Lists</Link>
          <div className="list-feed-header-actions">
            {isOwner && loaded && list && !list.is_favourite && (
              <>
                <button
                  type="button"
                  className="list-rename-btn"
                  onClick={() => {
                    setRenameValue(list.name);
                    setIsRenaming(true);
                  }}
                  disabled={savingRename || isRenaming}
                >
                  Rename
                </button>
                <button className="list-delete-btn" onClick={handleDeleteList} disabled={deleting || savingRename}>
                  {deleting ? '…' : 'Delete list'}
                </button>
              </>
            )}
          </div>
        </div>
        {isRenaming ? (
          <div className="list-feed-rename-form">
            <input
              type="text"
              className="list-feed-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              autoFocus
              disabled={savingRename}
              maxLength={100}
            />
            <button
              type="button"
              className="list-rename-submit-btn"
              onClick={handleRenameSubmit}
              disabled={savingRename || !renameValue.trim() || renameValue.trim() === list.name}
            >
              {savingRename ? '…' : 'Save'}
            </button>
            <button
              type="button"
              className="list-rename-cancel-btn"
              onClick={() => setIsRenaming(false)}
              disabled={savingRename}
            >
              Cancel
            </button>
          </div>
        ) : (
          <h1 className="list-feed-title">{listName}</h1>
        )}
        {loaded && list && (
          <p className="list-feed-meta">
            {list.members.length} {list.members.length === 1 ? 'author' : 'authors'}
          </p>
        )}

        {isOwner && loaded && list && (
          <div className="list-member-search-wrap">
            <input
              className="list-member-search-input"
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search Hive accounts to add (min 3 chars)…"
              autoComplete="off"
            />
            {searching && <div className="list-member-search-status">Searching…</div>}
            {!searching && searchValue.trim().length >= 3 && searchResults.length === 0 && (
              <div className="list-member-search-status">No matching accounts</div>
            )}
            {!searching && searchResults.length > 0 && (
              <ul className="list-member-search-results">
                {searchResults.map((account) => {
                  const normalized = String(account).toLowerCase();
                  return (
                    <li key={normalized} className="list-member-search-item">
                      <Link to={`/user/${normalized}`} className="list-member-search-link list-member-search-link-left">
                        <img
                          className="list-member-search-avatar"
                          src={`https://images.hive.blog/u/${normalized}/avatar/small`}
                          alt={normalized}
                          onError={e => { e.target.src = '/default-avatar.png'; }}
                        />
                        <span className="list-member-search-name">@{normalized}</span>
                      </Link>
                      <button
                        className="list-member-add-btn"
                        onClick={() => handleAddMemberFromSearch(normalized)}
                        disabled={Boolean(addingFromSearch[normalized])}
                      >
                        {addingFromSearch[normalized] ? 'Adding…' : 'Add'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="list-feed-content">
        {!loaded ? (
          <LoadingSpinner />
        ) : !list ? (
          <p className="list-feed-error">List not found.</p>
        ) : list.members.length === 0 ? (
          <p className="list-feed-empty">No members yet. Add authors to this list from a post's ⋯ menu.</p>
        ) : (
          <ul className="list-detail-members">
            {list.members.map(memberUsername => (
              <li key={memberUsername} className="list-detail-member-item">
                <Link to={`/user/${memberUsername}`} className="list-detail-member-link">
                  <img
                    className="list-detail-member-avatar"
                    src={`https://images.hive.blog/u/${memberUsername}/avatar/small`}
                    alt={memberUsername}
                    onError={e => { e.target.src = '/default-avatar.png'; }}
                  />
                  <span className="list-detail-member-name">@{memberUsername}</span>
                </Link>
                {isOwner && (
                  <button
                    className="list-detail-remove-btn"
                    onClick={() => handleRemoveMember(memberUsername)}
                    disabled={removing === memberUsername}
                    aria-label={`Remove @${memberUsername}`}
                  >
                    {removing === memberUsername ? '…' : '×'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ListDetail;

